import type * as THREE from "three";
import { dom } from "../dom";
import {
  COMPARE_ADDED_ID,
  COMPARE_CHANGED_ID,
  type IfcRevisionEntry,
  state,
} from "../state";
import {
  compareAddedMaterial,
  compareChangedMaterial,
  compareRemovedMaterial,
  ifcLoader,
  scene,
} from "../viewer/scene";
import { ensureRevisionSnapshot } from "./deferredInit";
import { setStatus } from "../ui/status";
import { buildRemovedGeometry, buildRevisionSnapshotFromFile } from "./revision";
import { getActiveFragmentsModel } from "../fragments/engine";

// THREE-объект «призрака» удалённых элементов (геометрия из базовой ревизии).
let removedGhostObject: THREE.Object3D | null = null;
// Защита от гонок: при быстрых переключениях галочки учитываем только
// последнюю операцию построения призрака.
let removedGhostBuildToken = 0;

/**
 * Резервный STEP-парсер: используется только когда IFC API недоступен
 * (например, baseline-файл не удалось распарсить через web-ifc) и для
 * экстренных диагностических случаев. Основной путь — `buildRevisionSnapshotFromFile`
 * через IFC API (см. revision.ts).
 *
 * По стандарту IFC, у IfcRoot первым атрибутом всегда идёт GlobalId, поэтому
 * regex по первой строковой константе работает для одно-строчных STEP-записей.
 */
export function parseIfcRevisionSnapshot(text: string): Map<string, IfcRevisionEntry> {
  const map = new Map<string, IfcRevisionEntry>();
  const lines = text.split(/\r?\n/);
  const rx = /^#(\d+)\s*=\s*([A-Z0-9_]+)\('([^']{8,})',(.*)\);\s*$/;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const m = line.match(rx);
    if (!m) {
      continue;
    }
    const expressId = Number(m[1]);
    const type = m[2];
    const guid = m[3];
    const fingerprint = `${type}|${m[4].replace(/\s+/g, " ").trim()}`;
    map.set(guid, { expressId, type, fingerprint });
  }
  return map;
}

export function updateCompareButtonsState(): void {
  const canCompare = Boolean(state.ifcModel && state.currentIfcRevision && state.baselineIfcRevision);
  dom.runCompareButton.disabled = !canCompare;
  dom.clearCompareButton.disabled = !state.ifcModel;
  const canPickBaseline = Boolean(state.ifcModel);
  dom.compareIfcInput.disabled = !canPickBaseline;
  dom.compareAddFromPcButton.disabled = !canPickBaseline;

  // Показываем имя текущей (основной) модели — что с чем сравниваем.
  if (dom.compareCurrentFileEl) {
    dom.compareCurrentFileEl.textContent = state.currentIfcFileName || "Модель не загружена";
  }

  // Блок выбора версии из истории СОД активен только при наличии загруженной
  // модели; его наполнение/видимость управляется отдельно (см. compareCde.ts).
  dom.compareCdeRefreshButton.disabled = !canPickBaseline;
  dom.compareCdeListSelect.disabled = !canPickBaseline;
  dom.compareCdeOpenButton.disabled = !canPickBaseline || !dom.compareCdeListSelect.value;

  // Галочка «показывать удалённые красным» доступна, когда есть что сравнивать.
  dom.compareShowRemovedToggle.disabled = !canCompare;
  dom.compareShowRemovedToggle.checked = state.showRemovedEnabled;
}

export function clearCompareHighlights(): void {
  if (!state.ifcModel) {
    return;
  }
  try {
    ifcLoader.ifcManager.removeSubset(state.ifcModel.modelID, compareAddedMaterial, COMPARE_ADDED_ID);
  } catch (_) {
    /* ignore */
  }
  try {
    ifcLoader.ifcManager.removeSubset(
      state.ifcModel.modelID,
      compareChangedMaterial,
      COMPARE_CHANGED_ID,
    );
  } catch (_) {
    /* ignore */
  }
}

/** Убирает «призрак» удалённых элементов из сцены и освобождает ресурсы. */
export function clearRemovedGhost(): void {
  removedGhostBuildToken++;
  if (!removedGhostObject) {
    return;
  }
  scene.remove(removedGhostObject);
  removedGhostObject.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }
  });
  removedGhostObject = null;
}

/**
 * Перестраивает «призрак» удалённых элементов по последнему результату
 * сравнения. Геометрия базовой ревизии строится лениво (web-ifc, только
 * удалённые элементы) и только когда включена галочка.
 */
async function refreshRemovedGhost(): Promise<void> {
  clearRemovedGhost();
  if (
    !state.showRemovedEnabled ||
    !state.ifcModel ||
    !state.baselineIfcFile ||
    state.lastRemovedExpressIds.length === 0
  ) {
    return;
  }
  const token = ++removedGhostBuildToken;
  const count = state.lastRemovedExpressIds.length;
  setStatus(`Построение геометрии удалённых элементов (${count})… для больших файлов это может занять время.`);
  try {
    const object = await buildRemovedGeometry(
      state.baselineIfcFile,
      state.lastRemovedExpressIds,
      compareRemovedMaterial,
    );
    // Пока строили — могли выключить галочку / сбросить сравнение.
    if (token !== removedGhostBuildToken || !state.showRemovedEnabled) {
      object?.traverse((node) => {
        const mesh = node as THREE.Mesh;
        mesh.geometry?.dispose();
      });
      return;
    }
    if (object) {
      removedGhostObject = object;
      scene.add(object);
      setStatus(`Удалённые элементы показаны красным (${count}).`);
    } else {
      setStatus("У удалённых элементов нет геометрии для отображения.");
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    setStatus(`Не удалось построить геометрию удалённых элементов: ${message}`, true);
  }
}

/** Вкл/выкл показ удалённых элементов красным (по галочке). */
export function setShowRemoved(enabled: boolean): void {
  state.showRemovedEnabled = enabled;
  if (!enabled) {
    clearRemovedGhost();
    return;
  }
  if (state.lastRemovedExpressIds.length === 0) {
    setStatus("Сначала выполните сравнение — затем удалённые элементы покажутся красным.");
    return;
  }
  void refreshRemovedGhost();
}

export function resetCompareState(resetBaselineFile = true): void {
  clearCompareHighlights();
  clearRemovedGhost();
  state.lastRemovedExpressIds = [];
  dom.compareStatsElement.textContent = "Сравнение не выполнено.";
  if (resetBaselineFile) {
    state.baselineIfcRevision = null;
    state.baselineIfcFileName = "";
    state.baselineIfcFile = null;
    dom.compareIfcInput.value = "";
    dom.compareBaselineFileEl.textContent = "Файл не выбран";
  }
  updateCompareButtonsState();
}

export async function applyCompareBaselineFromFile(file: File | undefined | null): Promise<void> {
  if (!file) {
    return;
  }
  setStatus(`Базовая ревизия: чтение ${file.name}...`);
  let snapshot: Map<string, IfcRevisionEntry> | null = null;
  // Основной путь — IFC API через web-ifc (учитывает IFCZIP, многострочные STEP,
  // и собирает fingerprint из реальных полей IfcRoot, а не из сырого текста).
  try {
    snapshot = await buildRevisionSnapshotFromFile(file);
  } catch (error) {
    console.warn(
      "Базовая ревизия: IFC API недоступен, переход на резервный regex-парсер.",
      error,
    );
  }
  if (!snapshot || snapshot.size === 0) {
    // Fallback: regex по STEP-тексту. Менее точно, но не требует загрузки модели.
    try {
      const text = await file.text();
      snapshot = parseIfcRevisionSnapshot(text);
    } catch (_) {
      setStatus("Не удалось прочитать базовый IFC файл.", true);
      return;
    }
  }
  state.baselineIfcRevision = snapshot;
  state.baselineIfcFileName = file.name;
  state.baselineIfcFile = file;
  // Новая база — прошлый «призрак» удалённых неактуален.
  clearRemovedGhost();
  state.lastRemovedExpressIds = [];
  const currentName = state.currentIfcFileName || "текущая модель";
  dom.compareStatsElement.textContent =
    `Текущая: ${currentName}\n` +
    `База: ${file.name}\n` +
    `Элементов с GlobalId в базе: ${snapshot.size}\n` +
    `Готово к запуску сравнения.`;
  dom.compareBaselineFileEl.textContent = file.name;
  updateCompareButtonsState();
  setStatus(`Базовая ревизия загружена: ${file.name}`);
}

/**
 * Преобразует GUID элементов в Fragments localId активной модели. Подсветка
 * выполняется по localId — а снимок ревизии хранит express id из web-ifc,
 * который НЕ совпадает с localId Fragments. Поэтому подсвечиваем строго по GUID
 * через getLocalIdsByGuids, иначе подсветка молча не появляется.
 */
async function resolveGuidsToLocalIds(guids: string[]): Promise<number[]> {
  if (!state.ifcModel || guids.length === 0) {
    return [];
  }
  const model = getActiveFragmentsModel(String(state.ifcModel.modelID));
  if (!model) {
    return [];
  }
  try {
    const local = await model.getLocalIdsByGuids(guids);
    return local.filter((v): v is number => typeof v === "number");
  } catch (_) {
    return [];
  }
}

async function applyCompareHighlights(addedGuids: string[], changedGuids: string[]): Promise<void> {
  if (!state.ifcModel) {
    return;
  }
  clearCompareHighlights();
  const addedIds = await resolveGuidsToLocalIds(addedGuids);
  const changedIds = await resolveGuidsToLocalIds(changedGuids);
  if (addedIds.length > 0) {
    await ifcLoader.ifcManager.createSubset({
      modelID: state.ifcModel.modelID,
      ids: addedIds,
      material: compareAddedMaterial,
      scene,
      removePrevious: true,
      customID: COMPARE_ADDED_ID,
    });
  }
  if (changedIds.length > 0) {
    await ifcLoader.ifcManager.createSubset({
      modelID: state.ifcModel.modelID,
      ids: changedIds,
      material: compareChangedMaterial,
      scene,
      removePrevious: true,
      customID: COMPARE_CHANGED_ID,
    });
  }
}

export function runIfcComparison(): void {
  if (!state.ifcModel || !state.baselineIfcRevision) {
    setStatus("Для сравнения загрузите текущую и базовую IFC.", true);
    return;
  }
  void ensureRevisionSnapshot().then(() => {
    if (!state.currentIfcRevision) {
      setStatus("Снимок текущей модели ещё не готов.", true);
      return;
    }
    void runIfcComparisonInternal();
  });
}

async function runIfcComparisonInternal(): Promise<void> {
  if (!state.ifcModel || !state.currentIfcRevision || !state.baselineIfcRevision) {
    return;
  }
  const added: string[] = [];
  const removed: string[] = [];
  const changed: string[] = [];
  const removedExpressIds: number[] = [];

  for (const [guid, curr] of state.currentIfcRevision) {
    const base = state.baselineIfcRevision.get(guid);
    if (!base) {
      added.push(guid);
      continue;
    }
    if (curr.fingerprint !== base.fingerprint) {
      changed.push(guid);
    }
  }
  for (const [guid, base] of state.baselineIfcRevision) {
    if (!state.currentIfcRevision.has(guid)) {
      removed.push(guid);
      if (Number.isFinite(base.expressId) && base.expressId > 0) {
        removedExpressIds.push(base.expressId);
      }
    }
  }
  state.lastRemovedExpressIds = removedExpressIds;

  await applyCompareHighlights(added, changed);
  // Если включена галочка — обновляем красный «призрак» удалённых элементов.
  if (state.showRemovedEnabled) {
    void refreshRemovedGhost();
  }
  const currentName = state.currentIfcFileName || dom.compareCurrentFileEl?.textContent || "текущая модель";
  dom.compareStatsElement.textContent =
    `Текущая: ${currentName}\n` +
    `База: ${state.baselineIfcFileName || "выбранный файл"}\n` +
    `Добавлено (нет в базе): ${added.length}\n` +
    `Удалено (есть только в базе): ${removed.length}\n` +
    `Изменено: ${changed.length}\n` +
    `Подсветка: зелёный — добавленные, оранжевый — изменённые`;
  setStatus(`Сравнение IFC выполнено: +${added.length} / -${removed.length} / ~${changed.length}`);
}
